// src/components/PricingFAQ.jsx
import React, { useState } from 'react';

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mb-4 overflow-hidden border-b border-gray-100">
      <button 
        className="w-full flex justify-between items-center py-5 text-left focus:outline-none" 
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-medium text-gray-800">{question}</h3>
        <span className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`}>
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </span>
      </button>
      
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <p className="pb-5 text-gray-600">
          {answer}
        </p>
      </div>
    </div>
  );
};

const PricingFAQ = () => {
  const faqItems = [
    {
      question: "Can I change plans later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes to your subscription will take effect on your next billing cycle. Simply log in to your account and visit the subscription management page to make changes. All subscription changes are processed through Stripe, our secure payment provider."
    },
    {
      question: "Do you offer discounts for non-profits or educational institutions?",
      answer: "Yes, we offer special pricing for qualified non-profit organizations and educational institutions. Please contact our sales team for more information about our discount programs and to verify eligibility."
    },
    {
      question: "How does billing work?",
      answer: "We use secure payment processing through Stripe. You'll be billed automatically at the start of each billing cycle. For annual plans, you'll be billed once per year. You can access your billing history and invoices at any time through your account dashboard."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, including Visa, Mastercard, American Express, and Discover. All payments are securely processed through Stripe."
    },
    {
      question: "How do I manage my subscription?",
      answer: "All subscriptions are managed through Stripe, our secure payment processor. You can access your subscription settings directly from your account dashboard. From there, you can update your payment information, change your plan, or cancel your subscription at any time."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel your subscription at any time from your account dashboard. When you cancel, you'll continue to have access to your paid features until the end of your current billing period. We don't offer partial refunds for unused time on your subscription, but you won't be charged again after cancellation."
    },
    {
      question: "Is my payment information secure?",
      answer: "Absolutely. We never store your credit card information on our servers. All payment processing is handled by Stripe, a PCI Service Provider Level 1 certified payment processor, which is the highest level of certification available in the payments industry."
    }
  ];

  return (
    <div className="max-w-3xl mx-auto py-16">
      <h2 className="text-2xl font-semibold mb-10 text-center">Frequently Asked Questions</h2>
      
      <div className="divide-y divide-gray-100">
        {faqItems.map((item, index) => (
          <FaqItem 
            key={index} 
            question={item.question} 
            answer={item.answer} 
          />
        ))}
      </div>
      
      <div className="mt-12 text-center py-8 px-6 bg-gray-50 rounded-lg">
        <h3 className="text-xl font-medium mb-3">Need help choosing the right plan?</h3>
        <p className="text-gray-600 mb-6 max-w-lg mx-auto">
          Our team is ready to help you find the perfect solution for your needs.
        </p>
        <button className="bg-black text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors">
          Contact Sales
        </button>
      </div>
    </div>
  );
};

export default PricingFAQ;